import { DEFAULT_PANEL_FIELDS } from 'features/charts-v2/constants/panel-defaults'
import {
  dropRidgelineBinDomain,
  resolveRidgelineMetrics,
} from 'features/charts-v2/ridgeline-artifact'
import { resolveSankeyMetrics } from 'features/charts-v2/sankey-artifact'
import { MAX_PANELS } from 'features/charts-v2/store'
import {
  type CustomBreakdown,
  type Dimension,
  mapDimensionToBreakdown,
  mapExpressionToMetric,
  mapFieldToDimension,
  type MetricType,
  type SelectedFilterOption,
  type TimeBucketOption,
} from 'features/charts-v2/types'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type {
  Breakdown,
  ChartPanel,
  ChartsV2State,
  DateRange,
} from 'features/charts-v2/types/filter-hash/v2'
import {
  RATING_ONLY_FIELD,
  foldRatingOnlyFilter,
} from 'features/charts-v2/utils/fold-rating-only-filter'
import { expandParentThemeNamesToSubThemeClusterUuids } from 'features/charts-v2/utils/theme-hierarchy'
import { buildSearchCriteriaFromLogical } from 'features/charts-v2/utils/translate-v1-filters-to-panel'
import { type FilterOption, FilterTypes } from 'features/filters/types'
import type { SearchTermsLogical } from 'types/project'

// ---------------------------------------------------------------------------
// Ask Yogi chart-artifact → charts-v2 vocabulary
//
// An Ask Yogi v2 chart artifact carries a resolved backend `identifier`
// (domains + aggregated_values), a chart-type key, and resolved filters /
// date range. These helpers translate that into charts-v2 panels so the
// artifact can be deep-linked into the charts-v2 page.
//
// The dimension/metric/breakdown mappers below are also consumed by the Ask
// Yogi `ArtifactChart` render path — they live here (charts-v2) because the
// vocabulary they target is charts-v2's, not Ask Yogi's.
// ---------------------------------------------------------------------------

export const CHART_TYPE_MAP: Record<string, ChartTypeV2> = {
  bar: ChartTypeV2.BAR_HORIZONTAL,
  line: ChartTypeV2.LINE,
  pie: ChartTypeV2.PIE,
  stacked_bar: ChartTypeV2.BAR_HORIZONTAL_STACKED,
  bubble: ChartTypeV2.BUBBLE,
  scatter: ChartTypeV2.SCATTER,
  heatmap: ChartTypeV2.HEATMAP,
  treemap: ChartTypeV2.TREEMAP,
  metrics_table: ChartTypeV2.TABLE,
  ridgeline: ChartTypeV2.RIDGELINE,
  sankey: ChartTypeV2.SANKEY,
}

export const TIME_BUCKET_MAP: Record<string, TimeBucketOption> = {
  dt_bucket_day: 'day',
  dt_bucket_week: 'week',
  dt_bucket_month: 'month',
  dt_bucket_quarter: 'quarter',
  dt_bucket_year: 'year',
}

export function getTimeBucket(
  domains: Array<{ fn?: string }>,
): TimeBucketOption | null {
  const timeDomain = domains.find((d) => d.fn && d.fn in TIME_BUCKET_MAP)
  return timeDomain?.fn ? (TIME_BUCKET_MAP[timeDomain.fn] ?? null) : null
}

function isCustomDimensionArg(
  arg: unknown,
): arg is { field: { dimension: string }; keys: string[] } {
  return (
    !!arg &&
    typeof arg === 'object' &&
    'field' in arg &&
    'keys' in arg &&
    !!arg.field &&
    typeof arg.field === 'object' &&
    'dimension' in arg.field
  )
}

// Dimensions that map to a groupable breakdown AND carry grouped semantics
// (parent-level roll-up rather than leaf-level). Derived from the source
// dimension so the converter can propagate the agent's grouping intent.
const GROUPED_DIMENSIONS = new Set([
  'parent_theme', // Dimension.PARENT_THEME
  'product_group', // Dimension.PRODUCT_GROUP
  'cluster_group', // Dimension.CLUSTER_GROUP
])

export function getBreakdownFromDomain(
  domain: { fn?: string; args?: unknown[] },
  customBreakdowns?: CustomBreakdown[],
): Breakdown | null {
  // Domains are serialized as {"fn": "identity", "args": [{"dimension": "parent_theme"}]}
  // Custom dimensions serialize as {"fn": "identity", "args": [{"field": {"dimension": "product_custom"}, "keys": ["Sub-Type"]}]}
  const arg = domain.args?.[0]

  if (isCustomDimensionArg(arg)) {
    const fieldKey = arg.keys[0]
    const matched = customBreakdowns?.find((cb) => cb.field === fieldKey)
    if (matched) {
      // Convert old CustomBreakdown to v2 CustomBreakdown with kind property
      return {
        kind: 'custom',
        field: matched.field,
        alias: matched.alias,
        model: matched.model,
      }
    }
    // Return null for unknown custom field instead of string
    return null
  }

  const dimensionStr =
    arg && typeof arg === 'object' && 'dimension' in arg
      ? (arg as { dimension: string }).dimension
      : typeof arg === 'string'
        ? arg
        : null
  if (!dimensionStr) return null

  const breakdownId = mapDimensionToBreakdown(dimensionStr as Dimension)
  if (!breakdownId) return null

  const isGroupable = ['brand', 'product', 'theme'].includes(breakdownId)
  const grouped = GROUPED_DIMENSIONS.has(dimensionStr)
  return {
    kind: 'standard',
    id: breakdownId,
    ...(isGroupable && { grouped }),
  } as Breakdown
}

/**
 * Maps the artifact's aggregated_values to charts-v2 metrics, falling back to
 * `record_count` when nothing maps. Used at the Ask Yogi render call site,
 * where a chart must always render *something*. The panel builder uses a
 * stricter rule (no mapped metric ⇒ no panel) and does NOT call this.
 */
export function getMetrics(
  aggregatedValues: Array<{ fn: string; args: unknown[] }>,
): MetricType[] {
  const metrics = aggregatedValues
    .map((expr) => mapExpressionToMetric(expr as never))
    .filter((m): m is MetricType => m !== null)
  return metrics.length > 0 ? metrics : ['record_count']
}

// ---------------------------------------------------------------------------
// Panel construction
// ---------------------------------------------------------------------------

// Chart types that render every metric at once (no single-metric toggle).
// Mirrors `ArtifactChart`'s `rendersAllMetrics`: axis + heatmap + table + line
// + treemap charts plot all aggregations together, so they emit ONE panel
// carrying all metrics. Line renders multi-metric as dual-axis (scored on left
// Y, volume on right Y); treemap renders agg[0] as box size, agg[1] as box
// color, and aggs[2-3] as additional data labels. Toggleable types
// (bar/pie/stacked_bar) emit ONE panel PER metric.
const RENDERS_ALL_METRICS_CHART_TYPES: ReadonlySet<ChartTypeV2> = new Set([
  ChartTypeV2.BUBBLE,
  ChartTypeV2.SCATTER,
  ChartTypeV2.HEATMAP,
  ChartTypeV2.TABLE,
  ChartTypeV2.LINE,
  ChartTypeV2.TREEMAP,
])

// charts-v2 panels require a dateRange. When the artifact carries no resolved
// date window (or only one bound), fall back to an all-time-ish open window so
// the page renders. Computed per call so `endDate` reflects click time rather
// than module-load time (which would stale in a long-lived tab).
function defaultDateRange(): DateRange {
  return {
    startDate: Math.floor(new Date(2018, 0, 1).getTime() / 1000),
    endDate: Math.floor(Date.now() / 1000),
  }
}

// The BE `resolved_filters[].field` is a filter-cache backend name (the output
// of `to_backend_criteria`). charts-v2 panel filters carry the *friendly* field
// id that `mapFieldToDimension` consumes. Most backend names already ARE valid
// friendly ids (`proj_uuid`, `theme`, `country`, `promotion`, `source_type`,
// `product_hierarchy`, `brand`, ...); the alias map introduces a handful that
// charts-v2 spells differently. Normalize those before the validity check.
const BACKEND_FILTER_FIELD_FIXUPS: Record<string, string> = {
  // `star_rating` range filter → backend `score`; charts-v2 spells it post_score.
  score: 'post_score',
}

/**
 * Translate one backend filter field name to a charts-v2 filter field id, or
 * null when charts-v2 has no field for it (the filter is dropped — broader data,
 * acceptable v1). Validity is gated on `mapFieldToDimension`: a field charts-v2
 * can't translate to a Dimension would be silently dropped at the page anyway.
 */
function toChartsV2FilterField(
  backendField: string,
  filterOptions?: FilterOption[],
): string | null {
  const candidate = BACKEND_FILTER_FIELD_FIXUPS[backendField] ?? backendField
  return mapFieldToDimension(candidate, filterOptions) !== null
    ? candidate
    : null
}

export type ResolvedSelectFilter = {
  field: string
  operator: 'in'
  values: string[]
}

export type ResolvedRangeFilter = {
  field: string
  min: number | null
  max: number | null
}

export type ResolvedFilter = ResolvedSelectFilter | ResolvedRangeFilter

function isRangeFilter(filter: ResolvedFilter): filter is ResolvedRangeFilter {
  // Discriminate on `operator`, which is present on every select filter and
  // absent on range filters. Keying off `min`/`max` presence would misclassify
  // a select filter the moment the wire shape ever grows those keys.
  return !('operator' in filter)
}

// Charts 2's theme filter contract is cluster_uuid; Ask Yogi sends display
// names. Resolve here so downstream emitters see the canonical shape.
function resolveThemeValuesToClusterUuids(
  values: string[],
  filterOptions?: FilterOption[],
): string[] {
  const themeOption = filterOptions?.find(
    (opt) => opt.field === 'theme' && opt.filter_type === FilterTypes.SELECT,
  )
  if (!themeOption || themeOption.filter_type !== FilterTypes.SELECT) {
    return values
  }
  const nameToClusterUuid = new Map<string, string>()
  for (const v of themeOption.values) {
    if (v.cluster_uuid) nameToClusterUuid.set(v.value, v.cluster_uuid)
  }
  return values.map((v) => nameToClusterUuid.get(v) ?? v)
}

function mapResolvedFilters(
  resolvedFilters: ResolvedFilter[],
  filterOptions?: FilterOption[],
): SelectedFilterOption[] {
  const selected: SelectedFilterOption[] = []

  for (const filter of resolvedFilters) {
    if (filter.field === 'parent_theme' && !isRangeFilter(filter)) {
      const expanded = expandParentThemeNamesToSubThemeClusterUuids(
        filter.values,
        filterOptions,
      )
      if (expanded.length > 0) {
        selected.push({ field: 'theme', operator: 'is', values: expanded })
      }
      continue
    }

    if (filter.field === RATING_ONLY_FIELD && !isRangeFilter(filter)) {
      // Kept verbatim (`toChartsV2FilterField` would drop it) and folded into
      // the score filter below, after every score range is in `selected`.
      selected.push({
        field: RATING_ONLY_FIELD,
        operator: 'is',
        values: filter.values,
      })
      continue
    }

    const field = toChartsV2FilterField(filter.field, filterOptions)
    if (field === null) continue

    if (isRangeFilter(filter)) {
      selected.push({
        field,
        operator: 'is',
        values: field === 'score' ? ['Null'] : [],
        min: filter.min ?? undefined,
        max: filter.max ?? undefined,
      })
    } else {
      const values =
        field === 'theme'
          ? resolveThemeValuesToClusterUuids(filter.values, filterOptions)
          : filter.values
      selected.push({
        field,
        operator: 'is',
        values,
      })
    }
  }

  return foldRatingOnlyFilter(selected)
}

function generatePanelId(): string {
  // No module-level counter: a random suffix keeps panel ids unique within a
  // build call without leaking mutable state across calls (or across tests).
  return `artifact-panel-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/**
 * A conformant artifact's panel, ready for the "View in charts" deep-link.
 *
 * The conformant chart tools return the PANEL they drew, so the artifact
 * already carries the thing charts-v2 wants. `chartArtifactToPanels` exists to
 * RECONSTRUCT a panel from the legacy artifact's scattered pieces —
 * `resolved_filters`, `date_range`, `domains`, `search_terms` — and that
 * reconstruction crosses a dialect boundary it has never crossed cleanly (see
 * this feature's AGENTS.md on `toChartsV2FilterField`). There is nothing to
 * reconstruct here.
 *
 * It was not always used. The conformant chart response sets `filters` (the
 * chips) but not `resolved_filters` or `date_range`, so the legacy builder read
 * their defaults — `[]` and undefined — and the deep-link opened an UNFILTERED
 * chart. No error; a real chart covering every record instead of the one on
 * screen.
 *
 * Only the presentation fields are added. Everything the query depends on comes
 * from the panel verbatim, which is the whole point.
 *
 * `title`, when provided, carries the chat artifact's own title through to the
 * charts-v2 page (rather than the page falling back to a strategy-derived
 * default) and marks it as custom so the page doesn't overwrite it.
 */
export function conformantArtifactToPanels(
  panel: ChartPanel,
  title?: string,
): ChartPanel[] {
  return [
    {
      ...DEFAULT_PANEL_FIELDS,
      ...panel,
      id: generatePanelId(),
      ...(title
        ? { title, isCustomTitle: true }
        : { title: '', isCustomTitle: false }),
      order: 0,
      presetId: null,
    },
  ]
}

export type ChartArtifactToPanelsArgs = {
  /** The artifact request's `chart_type` key (e.g. "bar", "bubble"). */
  chartTypeKey: string
  /** `identifier.domains` — breakdown + time-bucket expressions. */
  domains: Array<{ fn?: string; args?: unknown[] }>
  /** `identifier.aggregated_values` — the metric expressions. */
  aggregatedValues: Array<{ fn: string; args: unknown[] }>
  /** Resolved backend filters (BE `resolved_filters`); excludes the date window. */
  resolvedFilters?: ResolvedFilter[]
  /**
   * The chart's date window in epoch seconds (BE `date_range`). Either bound
   * may be `null` when the agent supplied an open-ended window; a null bound
   * falls back to the corresponding `DEFAULT_DATE_RANGE` edge.
   */
  dateRange?: { min: number | null; max: number | null } | null
  /**
   * The artifact request's logical `search_terms` block (`include_any`,
   * `include_all`, `exclude_all`, `exclude_all_of`). Mapped into the panel's
   * `searchCriteria` so keyword search round-trips into the charts-v2 page.
   */
  searchTermsLogical?: SearchTermsLogical
  /** Custom breakdowns from the project's filter list, for custom-dimension domains. */
  customBreakdowns?: CustomBreakdown[]
  /** Project filter options, used to resolve custom field filters (e.g. "Package Type"). */
  filterOptions?: FilterOption[]
  /**
   * The chat artifact's own title, when it has one. Passed through to the
   * built panel(s) as a custom title so the charts-v2 page doesn't fall back
   * to a strategy-derived default (e.g. "Summary Metrics Table").
   */
  title?: string
}

/**
 * Build charts-v2 panels from an Ask Yogi v2 chart artifact.
 *
 * Returns `[]` when nothing maps (no charts-v2 metric OR no breakdown), which is
 * the signal to hide the "View in charts" button. Uses a STRICTER rule than the
 * render path: a metric expression with no charts-v2 equivalent does not fall
 * back to `record_count`.
 *
 * Panel split mirrors `ArtifactChart`'s `rendersAllMetrics`: axis/heatmap/table/
 * line/treemap types emit one panel with all metrics (line renders multi-metric
 * as dual-axis; treemap as size/color/labels); toggleable types
 * (bar/pie/stacked_bar) emit one panel per metric (same breakdowns). Capped at
 * the charts-v2 store's 3-panel max.
 */
export function chartArtifactToPanels(
  args: ChartArtifactToPanelsArgs,
): ChartPanel[] {
  const chartType =
    CHART_TYPE_MAP[args.chartTypeKey] ?? ChartTypeV2.BAR_HORIZONTAL

  const timeBucket = getTimeBucket(args.domains)

  // Stricter than getMetrics: no record_count fallback.
  const wireMetrics = args.aggregatedValues
    .map((expr) => mapExpressionToMetric(expr as never))
    .filter((m): m is MetricType => m !== null)
  // A ridgeline / Sankey wire carries [count, avg] but draws ONE metric; split
  // per metric, the other half is a second panel reading "unsupported metric".
  const isRidgelineChart = chartType === ChartTypeV2.RIDGELINE
  const metrics = isRidgelineChart
    ? resolveRidgelineMetrics(undefined, wireMetrics)
    : chartType === ChartTypeV2.SANKEY
      ? resolveSankeyMetrics(undefined, wireMetrics)
      : wireMetrics

  const breakdowns = (
    isRidgelineChart ? dropRidgelineBinDomain(args.domains) : args.domains
  )
    .filter((d) => !d.fn || !(d.fn in TIME_BUCKET_MAP))
    .map((d) => getBreakdownFromDomain(d, args.customBreakdowns))
    .filter((b): b is Breakdown => b !== null)

  // A ridgeline with no series is one ridge, which the page draws as-is.
  if (metrics.length === 0 || (breakdowns.length === 0 && !isRidgelineChart)) {
    return []
  }

  const filters = mapResolvedFilters(
    args.resolvedFilters ?? [],
    args.filterOptions,
  )
  // A null bound (open-ended window from the agent) falls back to the
  // corresponding default edge so the panel always carries a numeric
  // DateRange. A fully-null/absent date_range falls back wholesale.
  const fallbackDateRange = defaultDateRange()
  const dateRange: DateRange | undefined = args.dateRange
    ? {
        startDate: args.dateRange.min ?? fallbackDateRange.startDate,
        endDate: args.dateRange.max ?? fallbackDateRange.endDate,
      }
    : undefined
  const searchCriteria = buildSearchCriteriaFromLogical(args.searchTermsLogical)

  const buildPanel = (
    panelMetrics: MetricType[],
    order: number,
  ): ChartPanel => ({
    ...DEFAULT_PANEL_FIELDS,
    id: generatePanelId(),
    ...(args.title
      ? { title: args.title, isCustomTitle: true }
      : { title: '', isCustomTitle: false }),
    order,
    chartType,
    metrics: panelMetrics,
    breakdowns,
    filters,
    ...(dateRange ? { dateRange } : { dateRange: fallbackDateRange }),
    timeBucket,
    searchCriteria,
    presetId: null,
  })

  const rendersAllMetrics = RENDERS_ALL_METRICS_CHART_TYPES.has(chartType)
  const panels = rendersAllMetrics
    ? [buildPanel(metrics, 0)]
    : metrics.map((metric, i) => buildPanel([metric], i))

  return panels.slice(0, MAX_PANELS)
}

/**
 * Wrap panels into a full ChartsV2State ready for `stateService.save`. One panel
 * set, default global settings, the first panel active unless told otherwise.
 */
export function buildChartsV2State(
  panels: ChartPanel[],
  activePanelId: string | null = panels[0]?.id ?? null,
): ChartsV2State {
  return {
    v2: {
      panels,
      globalSettings: {
        syncYAxis: true,
        syncXAxis: true,
        syncDisplaySettings: false,
      },
      activePanelId,
    },
  }
}
